/**
 * StorageStatCard Component
 * Statistics card for storage metrics with gradient styling
 */

import { forwardRef } from 'react';
import { Card, Stack, Text, Group } from '@/components/ui';
import { IconTrendingUp, IconTrendingDown, IconMinus } from '@tabler/icons-react';
import type { Icon } from '@tabler/icons-react';
import type { GradientVariant } from '@/components/ccms-components/types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export interface StorageStatCardProps {
  /** Title of the stat */
  title: string;
  /** Main value to display */
  value: string | number;
  /** Subtitle or secondary info */
  subtitle?: string;
  /** Icon to display */
  icon?: Icon;
  /** Gradient variant for styling */
  gradientVariant?: GradientVariant;
  /** Trend indicator */
  trend?: 'up' | 'down' | 'neutral';
  /** Trend value (e.g., "+12%") */
  trendValue?: string;
  /** Additional className */
  className?: string;
}

/**
 * Storage statistics card component with gradient accents
 *
 * @example
 * ```tsx
 * <StorageStatCard
 *   title="Total Files"
 *   value={1234}
 *   subtitle="Across all domains"
 *   icon={IconFiles}
 *   gradientVariant="primary"
 *   trend="up"
 *   trendValue="+12%"
 * />
 * ```
 */
export const StorageStatCard = forwardRef<HTMLDivElement, StorageStatCardProps>(
  (props, ref) => {
    const {
      title,
      value,
      subtitle,
      icon: Icon,
      gradientVariant = 'primary',
      trend,
      trendValue,
      className,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Get gradient colors based on variant
    const getGradientColor = (): string => {
      switch (gradientVariant) {
        case 'primary':
          return 'var(--mantine-color-blue-5)';
        case 'secondary':
          return 'var(--mantine-color-violet-5)';
        case 'tertiary':
          return 'var(--mantine-color-cyan-5)';
        case 'success':
          return 'var(--mantine-color-green-5)';
        case 'warning':
          return 'var(--mantine-color-yellow-5)';
        case 'danger':
          return 'var(--mantine-color-red-5)';
        case 'info':
          return 'var(--mantine-color-teal-5)';
        default:
          return 'var(--mantine-color-blue-5)';
      }
    };

    const getTrendIcon = () => {
      switch (trend) {
        case 'up':
          return <IconTrendingUp size={14} />;
        case 'down':
          return <IconTrendingDown size={14} />;
        case 'neutral':
          return <IconMinus size={14} />;
        default:
          return null;
      }
    };

    const getTrendColor = (): string => {
      switch (trend) {
        case 'up':
          return 'green';
        case 'down':
          return 'red';
        case 'neutral':
          return 'gray';
        default:
          return 'gray';
      }
    };

    const gradientColor = getGradientColor();

    return (
      <Card
        ref={ref}
        variant="elevated"
        hoverable
        hoverEffect="lift"
        style={{
          borderTop: `3px solid ${gradientColor}`,
        }}
        className={className}
      >
        <Stack gap="md">
          {/* Icon and Title */}
          <Group justify="space-between" align="flex-start">
            {Icon && (
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 'var(--mantine-radius-md)',
                  background: `linear-gradient(135deg, ${gradientColor}15, ${gradientColor}25)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon size={24} color={gradientColor} />
              </div>
            )}
            <Text size="sm" fw={500} c="dimmed" style={{ flex: 1, textAlign: Icon ? 'right' : 'left', ...accessibilityStyles }}>
              {title}
            </Text>
          </Group>

          {/* Value */}
          <Group justify="space-between" align="flex-end">
            <Text size="xl" fw={700} style={{ fontSize: '2rem', ...accessibilityStyles }}>
              {value}
            </Text>

            {/* Trend Indicator */}
            {trend && trendValue && (
              <Group gap={4} c={getTrendColor()}>
                {getTrendIcon()}
                <Text size="sm" fw={600} c={getTrendColor()} style={accessibilityStyles}>
                  {trendValue}
                </Text>
              </Group>
            )}
          </Group>

          {/* Subtitle */}
          {subtitle && (
            <Text size="xs" c="dimmed" style={accessibilityStyles}>
              {subtitle}
            </Text>
          )}
        </Stack>
      </Card>
    );
  }
);

StorageStatCard.displayName = 'StorageStatCard';

export default StorageStatCard;
